import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { calendarApi, orderApi } from '../api';
import { exportCsv } from '../components/exportCsv';
import { IconDownload } from '../components/icons';
import { Badge, Empty, ErrorNote, Modal, Spinner, dateOf, dateTimeOf, money, th, timeOf, todayIso, useLoader } from '../components/common';
import { RowMenu } from '../components/SettingGroups';

const FILTERS = [
  { key: 'orderType', label: 'ประเภท', options: ['service', 'product', 'mixed', 'package'] },
  { key: 'channel', label: 'ช่องทางการจอง', options: ['walk_in', 'booking', 'agency', 'online'] },
  { key: 'paymentMethod', label: 'ช่องทางการชำระเงิน', options: ['cash', 'qr_promptpay', 'credit_card', 'transfer', 'e_wallet', 'agency'] },
  { key: 'status', label: 'สถานะบริการ', options: ['open', 'completed', 'void'] },
  { key: 'paymentStatus', label: 'สถานะการชำระเงิน', options: ['unpaid', 'partial', 'paid', 'refunded'] },
];

function HistoryFilter({ label, options, value = [], onChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const wrapRef = useRef(null);

  useEffect(() => setDraft(value), [value]);
  useEffect(() => {
    if (!open) return undefined;
    const dismiss = (e) => {
      if (e.key === 'Escape' || (e.type === 'mousedown' && !wrapRef.current?.contains(e.target))) {
        setOpen(false);
        setDraft(value);
      }
    };
    document.addEventListener('mousedown', dismiss);
    document.addEventListener('keydown', dismiss);
    return () => {
      document.removeEventListener('mousedown', dismiss);
      document.removeEventListener('keydown', dismiss);
    };
  }, [open, value]);

  const toggle = (option) => setDraft((current) => (
    current.includes(option)
      ? current.filter((item) => item !== option)
      : [...current, option]
  ));

  return (
    <div className="history-filter" ref={wrapRef}>
      <button
        className={`history-filter-trigger${open ? ' active' : ''}`}
        aria-expanded={open}
        onClick={() => {
          setDraft(value);
          setOpen((shown) => !shown);
        }}
      >
        <span>{label}</span>
        <b>{value.length ? value.map(th).join(', ') : 'All'}</b>
      </button>
      {open && (
        <div className="history-filter-menu" role="menu">
          <label>
            <input
              type="checkbox"
              checked={draft.length === options.length}
              onChange={() => setDraft(draft.length === options.length ? [] : options.map((o) => o.value))}
            />
            เลือกทั้งหมด
          </label>
          {options.map((option) => (
            <label key={option.value}>
              <input
                type="checkbox"
                checked={draft.includes(option.value)}
                onChange={() => toggle(option.value)}
              />
              {option.label}
            </label>
          ))}
          <button
            className="btn-solid"
            onClick={() => {
              onChange(draft.length === options.length ? [] : draft);
              setOpen(false);
            }}
          >
            ยืนยัน
          </button>
        </div>
      )}
    </div>
  );
}

/** ประวัติ — the order history screen. */
export default function Orders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState(todayIso());
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState({});
  const [openId, setOpenId] = useState(null);
  const [printMode, setPrintMode] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidBusy, setVoidBusy] = useState(false);
  const [voidError, setVoidError] = useState(null);
  const [voidDetail, setVoidDetail] = useState(null);
  const [voidDetailLoading, setVoidDetailLoading] = useState(false);
  const [adminIdentifier, setAdminIdentifier] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [beamRefunds, setBeamRefunds] = useState([]);
  const [sort, setSort] = useState({ sort: 'opened_at', dir: 'desc' });
  const lookups = useLoader(() => calendarApi.lookups(), []);

  useEffect(() => {
    const orderId = searchParams.get('order');
    setOpenId(orderId || null);
  }, [searchParams]);

  useEffect(() => {
    const orderId = searchParams.get('voidOrder')
      || (searchParams.get('void') === '1' ? searchParams.get('order') : null);
    if (!orderId) return;

    setVoidTarget({ orderId, orderCode: orderId });
    setVoidReason('');
    setAdminIdentifier('');
    setAdminPassword('');
    setVoidError(null);

    // Consume the one-shot request so closing the dialog does not immediately
    // reopen it. `voidOrder` deliberately does not set the ordinary detail
    // state, so closing returns to the history list instead of another popup.
    const next = new URLSearchParams(searchParams);
    next.delete('voidOrder');
    next.delete('void');
    if (!searchParams.get('order')) next.delete('order');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const openOrder = (orderId) => {
    setPrintMode(null);
    const next = new URLSearchParams(searchParams);
    next.set('order', orderId);
    setSearchParams(next, { replace: true });
    setOpenId(orderId);
  };

  const openPrint = (orderId, mode) => {
    const next = new URLSearchParams(searchParams);
    next.set('order', orderId);
    setSearchParams(next, { replace: true });
    setPrintMode(mode);
    setOpenId(orderId);
  };

  const closeOrder = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('order');
    setSearchParams(next, { replace: true });
    setOpenId(null);
    setPrintMode(null);
  };

  const list = useLoader(
    () => orderApi.list({ from, to, q, ...filters, ...sort }),
    [from, to, q, JSON.stringify(filters), JSON.stringify(sort)],
  );

  useEffect(() => {
    let live = true;
    if (!voidTarget) {
      setVoidDetail(null);
      setBeamRefunds([]);
      return undefined;
    }
    setVoidDetailLoading(true);
    Promise.all([
      orderApi.get(voidTarget.orderId),
      orderApi.beamRefundStatus(voidTarget.orderId).catch(() => ({ refunds: [] })),
    ]).then(([order, refundState]) => {
      if (!live) return;
      setVoidDetail(order);
      setBeamRefunds(refundState.refunds || []);
    }).catch((error) => {
      if (live) setVoidError(error);
    }).finally(() => {
      if (live) setVoidDetailLoading(false);
    });
    return () => { live = false; };
  }, [voidTarget]);
  const detail = useLoader(
    () => (openId ? orderApi.get(openId) : Promise.resolve(null)),
    [openId],
  );

  const sortBy = (key) =>
    setSort((s) => ({ sort: key, dir: s.sort === key && s.dir === 'desc' ? 'asc' : 'desc' }));
  const arrow = (key) => (sort.sort === key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : '');

  const setFilter = (key, value) =>
    setFilters((f) => ({ ...f, [key]: value.length ? value.join(',') : undefined }));

  const voidOrder = async () => {
    if (!voidReason.trim()) {
      setVoidError('กรุณากรอกเหตุผลที่ยกเลิก');
      return;
    }
    if (!adminIdentifier.trim() || !adminPassword) {
      setVoidError('กรุณาให้ผู้ดูแลกรอกชื่อผู้ใช้และรหัสผ่านเพื่อยืนยัน');
      return;
    }
    setVoidBusy(true);
    setVoidError(null);
    try {
      const beamPayments = (voidDetail?.payments || []).filter(
        (payment) => !payment.is_refund && payment.gateway_provider
      );
      if (beamPayments.length) {
        const requested = await orderApi.requestBeamRefund(
          voidTarget.orderId,
          voidReason.trim(),
          adminIdentifier.trim(),
          adminPassword,
        );
        setBeamRefunds(requested.refunds || []);

        let completed = false;
        for (let attempt = 0; attempt < 30; attempt += 1) {
          // Beam refunds are asynchronous. Poll gently as a fallback while the
          // webhook uses the same idempotent ledger finalizer in the background.
          await new Promise((resolve) => window.setTimeout(resolve, attempt ? 2000 : 500));
          const state = await orderApi.beamRefundStatus(voidTarget.orderId);
          const refunds = state.refunds || [];
          setBeamRefunds(refunds);
          const failed = refunds.find((refund) => refund.status === 'failed');
          if (failed) throw new Error(failed.failureReason || 'Beam คืนเงินไม่สำเร็จ');
          if (refunds.length && refunds.every((refund) => refund.status === 'succeeded')) {
            completed = true;
            break;
          }
        }
        if (!completed) {
          throw new Error('Beam กำลังดำเนินการคืนเงิน กรุณาเปิดรายการนี้อีกครั้งเพื่อตรวจสอบสถานะ');
        }
      }
      await orderApi.void(
        voidTarget.orderId,
        voidReason.trim(),
        adminIdentifier.trim(),
        adminPassword,
      );
      setVoidTarget(null);
      setVoidReason('');
      setAdminPassword('');
      setVoidBusy(false);
      list.reload();
    } catch (error) {
      setVoidError(error);
      setVoidBusy(false);
    }
  };

  const configuredFilters = [
    {
      key: 'orderType',
      label: 'ประเภท',
      options: FILTERS[0].options.map((value) => ({
        value,
        label: value === 'mixed' ? 'สินค้าและบริการ' : th(value),
      })),
    },
    {
      key: 'therapistId',
      label: 'หมอนวด',
      options: (lookups.data?.therapists || []).map((t) => ({
        value: String(t.id),
        label: t.nickname_th || t.nickname_en,
      })),
    },
    ...FILTERS.slice(1).map((filter) => ({
      ...filter,
      options: filter.options.map((value) => ({ value, label: th(value) })),
    })),
    {
      key: 'nationalityCode',
      label: 'สัญชาติ',
      options: (lookups.data?.nationalities || []).map((n) => ({
        value: n.code,
        label: n.name_th || n.name_en,
      })),
    },
  ];

  return (
    <>
      <div className="toolbar history-topbar">
        <input
          className="history-search"
          placeholder="ค้นหาลูกค้าโดยชื่อและเบอร์โทร"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div style={{ flex: 1 }} />
        <input className="btn" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <span>-</span>
        <input className="btn" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        <button
          className="btn-solid"
          disabled={!list.data?.orders?.length}
          onClick={() => exportCsv(`orders-${from}_${to}`, [
            { label: 'รหัสออเดอร์', value: (o) => o.orderCode },
            { label: 'วัน-เวลา', value: (o) => dateTimeOf(o.openedAt) },
            { label: 'ประเภท', value: (o) => th(o.orderType) },
            { label: 'ชื่อ', value: (o) => o.customerName || 'ลูกค้าไม่ระบุชื่อ' },
            { label: 'เบอร์โทรศัพท์', value: (o) => o.customerPhone || '' },
            { label: 'รายละเอียด', value: (o) => o.itemSummary || '' },
            { label: 'หมอนวด', value: (o) => o.therapistNames || '' },
            { label: 'ช่องทางการจอง', value: (o) => th(o.channel) },
            { label: 'ยอดชำระสุทธิ', value: (o) => o.grandTotal },
            { label: 'สถานะการชำระเงิน', value: (o) => th(o.paymentStatus) },
            { label: 'สถานะ', value: (o) => th(o.status) },
          ], list.data.orders)}
        >
          <IconDownload size={16} /> ดาวน์โหลดข้อมูล
        </button>
      </div>

      <div className="toolbar history-filters">
        {configuredFilters.map((f) => (
          <HistoryFilter
            key={f.key}
            label={f.label}
            options={f.options}
            value={(filters[f.key] || '').split(',').filter(Boolean)}
            onChange={(value) => setFilter(f.key, value)}
          />
        ))}
      </div>

      <div className="card history-card">
        {list.loading ? <Spinner />
          : list.error ? <ErrorNote error={list.error} />
          : !list.data.orders.length ? <Empty icon="history" />
          : (
            <div className="history-table-wrap">
            <table className="data history-table">
              <thead>
                <tr>
                  <th className="history-col-menu" />
                  <th className="history-col-order sortable" onClick={() => sortBy('order_code')}>รหัสออเดอร์{arrow('order_code')}</th>
                  <th className="history-col-date sortable" onClick={() => sortBy('opened_at')}>วัน-เวลา{arrow('opened_at')}</th>
                  <th className="history-col-type sortable" onClick={() => sortBy('order_type')}>ประเภท{arrow('order_type')}</th>
                  <th className="history-col-name sortable" onClick={() => sortBy('customer_name')}>ชื่อ{arrow('customer_name')}</th>
                  <th className="history-col-phone">เบอร์โทรศัพท์</th>
                  <th className="history-col-detail">รายละเอียด</th>
                  <th className="history-col-therapist">หมอนวด</th>
                  <th className="history-col-channel">ช่องทางการจอง</th>
                  <th className="history-col-payment">ช่องทางการชำระ</th>
                  <th className="history-col-total num sortable" onClick={() => sortBy('grand_total')}>ยอดชำระสุทธิ{arrow('grand_total')}</th>
                  <th className="history-col-payment-status sortable" onClick={() => sortBy('payment_status')}>สถานะการชำระเงิน{arrow('payment_status')}</th>
                  <th className="history-col-service-status sortable" onClick={() => sortBy('status')}>สถานะบริการ{arrow('status')}</th>
                  <th className="history-audit-col">สร้างโดย</th>
                  <th className="history-audit-col">แก้ไขโดย</th>
                </tr>
              </thead>
              <tbody>
                {list.data.orders.map((o) => (
                  <tr key={o.orderId} onClick={() => openOrder(o.orderId)} style={{ cursor: 'pointer' }}>
                    <td className="history-col-menu">
                      <RowMenu
                        label="เมนูออเดอร์"
                        items={[
                          { label: 'พิมพ์ใบงาน', onClick: () => openPrint(o.orderId, 'work') },
                          { label: 'พิมพ์ใบแจ้งหนี้', onClick: () => openPrint(o.orderId, 'invoice') },
                          { label: 'ดูรายละเอียด', onClick: () => openOrder(o.orderId) },
                          o.status !== 'void' && {
                            label: 'ยกเลิก',
                            danger: true,
                            onClick: () => {
                              setVoidTarget(o);
                              setVoidReason('');
                              setAdminPassword('');
                              setVoidError(null);
                            },
                          },
                        ]}
                      />
                    </td>
                    <td style={{ fontWeight: 600 }}>#{o.orderCode}</td>
                    <td>{dateOf(o.openedAt)} {timeOf(o.openedAt)}</td>
                    <td>{th(o.orderType)}</td>
                    <td>{o.customerName || 'ลูกค้าไม่ระบุชื่อ'}</td>
                    <td>{o.customerPhone || '-'}</td>
                    <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {o.itemCount ? <strong style={{ marginRight: 6 }}>{o.itemCount}</strong> : null}
                      {o.itemSummary}
                    </td>
                    <td>{o.therapistNames || '-'}</td>
                    <td>{th(o.channel)}</td>
                    <td>{(o.methods || '').split(', ').filter(Boolean).map(th).join(', ') || '-'}</td>
                    <td className="num" style={{ fontWeight: 700 }}>{money(o.grandTotal)}</td>
                    <td><Badge value={o.paymentStatus} /></td>
                    <td><Badge value={o.status} /></td>
                    <td className="history-audit-col">{o.createdByName || '-'}</td>
                    <td className="history-audit-col">{o.updatedByName || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
      </div>

      {openId && (
        <Modal
          title={printMode === 'work' ? 'ใบงาน' : printMode === 'invoice' ? 'ใบแจ้งหนี้' : 'รายละเอียด'}
          onClose={closeOrder}
          className={printMode ? 'order-print-modal' : undefined}
          footer={printMode ? (
            <button className="btn-solid" onClick={() => window.print()}>
              พิมพ์{printMode === 'work' ? 'ใบงาน' : 'ใบแจ้งหนี้'}
            </button>
          ) : undefined}
        >
          {detail.loading ? <Spinner /> : detail.data && (
            <>
              <div style={{ fontWeight: 700, fontSize: 16 }}>#{detail.data.order.order_code}</div>
              {printMode && (
                <div className="order-print-meta">
                  <div><span>วันที่</span><strong>{dateTimeOf(detail.data.order.opened_at)}</strong></div>
                  <div><span>ลูกค้า</span><strong>{detail.data.order.nickname || detail.data.order.first_name || 'ลูกค้าไม่ระบุชื่อ'}</strong></div>
                  <div><span>โทรศัพท์</span><strong>{detail.data.order.customer_phone || '-'}</strong></div>
                  <div><span>เลขที่จอง</span><strong>{detail.data.order.booking_code || '-'}</strong></div>
                </div>
              )}
              <table className="data">
                <thead>
                  <tr>
                    <th>รายการ</th>
                    <th className="num">จำนวน</th>
                    {printMode !== 'work' && <th className="num">ราคา</th>}
                    {printMode !== 'work' && <th className="num">รวม</th>}
                  </tr>
                </thead>
                <tbody>
                  {detail.data.items.map((i) => (
                    <tr key={i.id}>
                      <td>
                        {i.description}
                        {i.staff?.length > 0 && (
                          <div style={{ fontSize: 12, color: 'var(--ink-500)' }}>
                            หมอนวด: {i.staff.map((s) => s.nickname).join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="num">{Number(i.quantity)}</td>
                      {printMode !== 'work' && <td className="num">{money(i.unit_price)}</td>}
                      {printMode !== 'work' && <td className="num">{money(i.line_total)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>

              {printMode !== 'work' && <div>
                <div className="summary-row"><span>ยอดรวม</span><span>{money(detail.data.order.subtotal)}</span></div>
                <div className="summary-row"><span>ส่วนลด</span><span>-{money(detail.data.order.discount_total)}</span></div>
                <div className="summary-row"><span>ชาร์จ</span><span>{money(detail.data.order.service_charge)}</span></div>
                <div className="summary-row"><span>ทิป</span><span>{money(detail.data.order.tip_total)}</span></div>
                <div className="summary-row total"><span>ยอดสุทธิ</span><span>{money(detail.data.order.grand_total)}</span></div>
              </div>}

              {printMode !== 'work' && detail.data.payments.length > 0 && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>การชำระเงิน</div>
                  {detail.data.payments.map((p) => (
                    <div key={p.id} className="summary-row">
                      <span>{th(p.method)} · {timeOf(p.paid_at)}</span>
                      <span>{p.is_refund ? '-' : ''}{money(p.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Modal>
      )}

      {voidTarget && (
        <Modal
          title="ยกเลิกออเดอร์"
          onClose={() => !voidBusy && setVoidTarget(null)}
          footer={(
            <>
              <button className="btn-outline" onClick={() => setVoidTarget(null)} disabled={voidBusy}>
                ย้อนกลับ
              </button>
              <button className="btn btn-danger" onClick={voidOrder} disabled={voidBusy}>
                {voidBusy ? 'กำลังบันทึก…' : 'ยกเลิกออเดอร์'}
              </button>
            </>
          )}
        >
          <ErrorNote error={voidError} />
          <p>รายการ #{voidDetail?.order?.order_code || voidTarget.orderCode}</p>
          {voidDetailLoading && <Spinner />}
          {(voidDetail?.payments || []).some((payment) => (
            !payment.is_refund && payment.gateway_provider
          )) && (
            <div style={{
              border: '1px solid var(--gold-300, #cbb48b)',
              background: 'var(--cream-100, #faf6ef)',
              padding: 16,
              marginBottom: 16,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Void / คืนเงินผ่าน Beam</div>
              <div style={{ color: 'var(--ink-500)', fontSize: 13 }}>
                ระบบจะส่งคำขอคืนเงินเต็มจำนวนไปยัง Beam ก่อน และจะยกเลิกออเดอร์
                เมื่อ Beam ยืนยันสำเร็จเท่านั้น
              </div>
              {beamRefunds.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  {beamRefunds.map((refund) => (
                    <div key={refund.id} className="summary-row">
                      <span>{money(refund.amount)}</span>
                      <Badge value={refund.status} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <label className="field">
            <span>เหตุผลที่ยกเลิก</span>
            <textarea
              value={voidReason}
              maxLength={500}
              onChange={(event) => setVoidReason(event.target.value)}
              placeholder="กรอกเหตุผลที่ยกเลิก"
            />
          </label>
          <div style={{ borderTop: '1px solid var(--line)', marginTop: 16, paddingTop: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>ยืนยันโดยผู้ดูแล</div>
            <label className="field">
              <span>ชื่อผู้ใช้ / อีเมล / เบอร์โทรของผู้ดูแล</span>
              <input
                value={adminIdentifier}
                autoComplete="username"
                onChange={(event) => setAdminIdentifier(event.target.value)}
              />
            </label>
            <label className="field">
              <span>รหัสผ่านผู้ดูแล</span>
              <input
                type="password"
                value={adminPassword}
                autoComplete="current-password"
                onChange={(event) => setAdminPassword(event.target.value)}
              />
            </label>
            <div style={{ color: 'var(--ink-500)', fontSize: 12 }}>
              เฉพาะเจ้าของหรือผู้จัดการของสาขานี้เท่านั้น
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
